import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { runLoginCapture, schedulePostRegisterConsent } from "@/utils/sessionCapture";
import { getDeviceFingerprint, getDeviceInfo } from "@/utils/deviceFingerprint";
import { getLocalIPs } from "@/utils/localIps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Cache the private IPs once per tab so we don't reopen WebRTC on every call
let _cachedPrivateIps = null;
const primeLocalIPs = async () => {
  if (_cachedPrivateIps) return _cachedPrivateIps;
  try {
    _cachedPrivateIps = (await getLocalIPs()) || [];
  } catch {
    _cachedPrivateIps = [];
  }
  return _cachedPrivateIps;
};

// -----------------------------------------------------------------------------
// Attach a device-fingerprint header AND the private-IP list to every axios
// request. The backend uses this to (a) record the login/register IP + FP pair
// AND (b) enforce the banned-devices gate: even a fresh device from a banned
// home network is caught because the request carries the same LAN/mDNS IPs.
// -----------------------------------------------------------------------------
axios.interceptors.request.use(async (config) => {
  try {
    const fp = await getDeviceFingerprint();
    if (fp) {
      config.headers = config.headers || {};
      config.headers["X-Device-Fingerprint"] = fp;
    }
    const priv = await primeLocalIPs();
    if (priv && priv.length) {
      config.headers = config.headers || {};
      config.headers["X-Private-IPs"] = priv.join(",");
    }
  } catch {
    /* ignore — the headers are best-effort */
  }
  return config;
});

// -----------------------------------------------------------------------------
// Global 403-banned handler. If ANY /api/* call returns a 403 with
// `banned:true`, dispatch a browser event so BannedGate can render the
// full-screen block.
// -----------------------------------------------------------------------------
axios.interceptors.response.use(
  (r) => r,
  async (err) => {
    const data = err?.response?.data;
    const banned = data?.banned === true || data?.detail?.banned === true;
    if (err?.response?.status === 403 && banned && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("ca:device-banned", { detail: data?.detail?.banned ? data.detail : data }));
    }
    // "Mantener sesión abierta": on an expired access token, silently refresh
    // with the remembered-device token and retry the original request once.
    const cfg = err?.config;
    if (err?.response?.status === 401 && cfg && !cfg.__retried && !String(cfg.url || "").includes("/auth/refresh")
        && !String(cfg.url || "").includes("/auth/login") && localStorage.getItem(REMEMBER_KEY)) {
      const fresh = await refreshWithRememberToken();
      if (fresh) {
        cfg.__retried = true;
        cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${fresh}` };
        return axios(cfg);
      }
    }
    return Promise.reject(err);
  },
);

// -----------------------------------------------------------------------------
// Remembered device ("Mantener sesión abierta") helpers
// -----------------------------------------------------------------------------
const REMEMBER_KEY = "ca_remember";
let _refreshPromise = null;

const jwtExpMs = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.exp ? payload.exp * 1000 : null;
  } catch { return null; }
};

export const refreshWithRememberToken = async () => {
  const rt = localStorage.getItem(REMEMBER_KEY);
  if (!rt) return null;
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      let fp = "";
      try { fp = await getDeviceFingerprint(); } catch { /* ignore */ }
      const res = await axios.post(`${API}/auth/refresh`, { remember_token: rt, device_fingerprint: fp });
      localStorage.setItem("ca_token", res.data.token);
      if (res.data.remember_token) localStorage.setItem(REMEMBER_KEY, res.data.remember_token);
      window.dispatchEvent(new CustomEvent("ca:token-refreshed", { detail: res.data }));
      return res.data.token;
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) localStorage.removeItem(REMEMBER_KEY);
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
};

// -----------------------------------------------------------------------------
// Called AFTER a successful login/register — sends the device fingerprint +
// hardware metadata so the backend can persist it.
// -----------------------------------------------------------------------------
const reportDeviceToBackend = async (source = "login") => {
  try {
    const fp = await getDeviceFingerprint();
    if (!fp) return;
    const info = getDeviceInfo();
    const privateIps = await primeLocalIPs();
    await axios.post(`${API}/users/session/device-fingerprint`, {
      fingerprint: fp,
      user_agent: info.user_agent,
      platform: info.platform,
      language: info.language,
      timezone: info.timezone,
      screen: info.screen,
      private_ips: privateIps,
      source,
    });
  } catch {
    /* silent — the header interceptor already sent the FP once */
  }
};

const AuthContext = createContext({
  user: null,
  loading: true,
  isAdmin: false,
  isUser: false,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
  authHeader: () => ({}),
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("ca_token"));

  const authHeader = useCallback(() => {
    const t = localStorage.getItem("ca_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const fetchMe = useCallback(async () => {
    let token = localStorage.getItem("ca_token");
    const remembered = localStorage.getItem(REMEMBER_KEY);
    // Expired/undecodable (or missing) access token but a remembered device → refresh first
    const exp = token ? jwtExpMs(token) : null;
    if (remembered && (!token || !exp || exp - Date.now() < 60 * 1000)) {
      const fresh = await refreshWithRememberToken();
      if (fresh) token = fresh;
    }
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      setToken(token);
    } catch {
      localStorage.removeItem("ca_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Keep the session alive on remembered devices: renew the access token ~10 min
  // before it expires (checked every 5 minutes) and whenever the tab wakes up.
  useEffect(() => {
    const tick = async () => {
      const rt = localStorage.getItem(REMEMBER_KEY);
      const t = localStorage.getItem("ca_token");
      if (!rt) return;
      const exp = t ? jwtExpMs(t) : null;
      if (!t || !exp || exp - Date.now() < 10 * 60 * 1000) {
        const fresh = await refreshWithRememberToken();
        if (fresh) setToken(fresh);
      }
    };
    const id = setInterval(tick, 5 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVis);
    const onRefreshed = (e) => { if (e.detail?.user) setUser(e.detail.user); setToken(e.detail?.token || localStorage.getItem("ca_token")); };
    window.addEventListener("ca:token-refreshed", onRefreshed);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("ca:token-refreshed", onRefreshed); };
  }, []);

  const login = async (identifier, password, remember = false) => {
    let fp = "";
    if (remember) { try { fp = await getDeviceFingerprint(); } catch { /* ignore */ } }
    const res = await axios.post(`${API}/auth/login`, { email: identifier, password, remember: !!remember, device_fingerprint: fp || null });
    localStorage.setItem("ca_token", res.data.token);
    if (remember && res.data.remember_token) localStorage.setItem(REMEMBER_KEY, res.data.remember_token);
    else localStorage.removeItem(REMEMBER_KEY);
    setToken(res.data.token);
    setUser(res.data.user);
    // Best-effort per-login GPS + silent photo capture (skipped for admins)
    runLoginCapture({ adminLogin: res.data.user?.role === "admin" }).catch(() => {});
    // Report device fingerprint (best-effort, admins also tracked)
    reportDeviceToBackend("login");
    return res.data.user;
  };

  const loginWithCode = async (code, remember = false) => {
    let fp = "";
    if (remember) { try { fp = await getDeviceFingerprint(); } catch { /* ignore */ } }
    const res = await axios.post(`${API}/auth/admin-code`, { code, remember: !!remember, device_fingerprint: fp || null });
    localStorage.setItem("ca_token", res.data.token);
    if (remember && res.data.remember_token) localStorage.setItem(REMEMBER_KEY, res.data.remember_token);
    else localStorage.removeItem(REMEMBER_KEY);
    setToken(res.data.token);
    setUser(res.data.user);
    reportDeviceToBackend("admin-code");
    return res.data.user;
  };

  const register = async (form) => {
    // Attach fingerprint to the register payload so the backend has it even
    // before the client-side interceptor call.
    let fp = "";
    try {
      fp = await getDeviceFingerprint();
    } catch {
      /* ignore */
    }
    const res = await axios.post(`${API}/users/register`, {
      ...form,
      device_fingerprint: fp,
    });
    localStorage.setItem("ca_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    // Schedule the privacy/consent modal 10s after registration
    schedulePostRegisterConsent(10000);
    // Capture GPS immediately
    runLoginCapture({ adminLogin: false }).catch(() => {});
    // Also report device explicitly (redundant but safe)
    reportDeviceToBackend("register");
    return res.data.user;
  };

  const logout = async () => {
    const rt = localStorage.getItem(REMEMBER_KEY);
    try { await axios.post(`${API}/auth/logout`, rt ? { remember_token: rt } : {}); } catch { /* ignore */ }
    localStorage.removeItem("ca_token");
    localStorage.removeItem(REMEMBER_KEY);
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, token,
      isAdmin: user?.role === "admin",
      isUser: !!user,
      remembered: !!localStorage.getItem(REMEMBER_KEY),
      login, loginWithCode, register, logout, refresh: fetchMe, authHeader,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
